import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  BseDto,
  BseQuestionDto,
  BseQuestionStatus,
  EmailInviteDto,
  NegotiationContextDto,
  NegotiationRoundDto,
  OrganizationInviteDto,
  ProductGroupBidDto,
  ProjectDto,
  ReceivedBseBidDto,
  UploadDto,
  ViewDocumentAsOwnerDto,
  WinningProductGroupBidsDto,
} from "../../../autogen/bff-api";
import {
  addBidId,
  findOrCreateGroupBids,
  findOrCreateProductBids,
  removeBidId,
} from "../../../pages/sourcing-events/buyer/view-single/bid-evaluation/BidEvaluationUtils";
import { ProductGroup } from "../../types";
import { editBseThunk } from "../thunks/basic-sourcing-event/edit-bse-thunk";
import { loadBseThunk } from "../thunks/basic-sourcing-event/load-bse-thunk";
import { isOlderRevision, isOlderRevisionOfSameEntity } from "./staleResponseGuard";

export type BasicSourcingEventState = {
  id: string;
  event: BseDto;
  lastChanged: string;
  title: {
    value: string;
    errorMessage: string | null;
  };
  description: {
    value: string | null;
    errorMessage: string | null;
  };
  deadline: {
    value?: string | null;
    errorMessage: string | null;
  };
  timezone: {
    value: string;
    errorMessage: string | null;
  };
  referenceId: {
    value: string | null;
    errorMessage: string | null;
  };
  emailInvites: {
    value: EmailInviteDto[];
    errorMessage: string | null;
  };
  organizationInvites: {
    value: OrganizationInviteDto[];
    errorMessage: string | null;
  };
  documents: {
    value: ViewDocumentAsOwnerDto[];
    errorMessage: string | null;
  };
  uploads: {
    value: UploadDto[];
    errorMessage: string | null;
  };
  projects: {
    values?: ProjectDto[] | null;
    errorMessage: string | null;
  };
};

interface State {
  state: BasicSourcingEventState | null;
  requestedEventId?: string | null;
}

const initialState: State = {
  state: null,
};

export const slice = createSlice<
  State,
  {
    setSourcingEvent: (state: State, action: PayloadAction<BseDto | null>) => void;
    selectBid: (
      state: State,
      action: PayloadAction<{
        bid: ReceivedBseBidDto;
        productId: string;
        group: ProductGroup;
        negotiationRoundId?: string;
      }>
    ) => void;
    selectAllBids: (
      state: State,
      action: PayloadAction<{ bid: ReceivedBseBidDto; negotiationRoundId?: string }>
    ) => void;
    selectAllBidsInGroup: (
      state: State,
      action: PayloadAction<{ bid: ReceivedBseBidDto; groupBid: ProductGroupBidDto; negotiationRoundId?: string }>
    ) => void;
    removeBid: (
      state: State,
      action: PayloadAction<{
        bid: ReceivedBseBidDto;
        productId: string;
        group: ProductGroup;
        negotiationRoundId?: string;
      }>
    ) => void;
    setSupplierQuestionStatus: (
      state: State,
      action: PayloadAction<{ question: BseQuestionDto; status: BseQuestionStatus }>
    ) => void;
    setNegotiationRound: (state: State, action: PayloadAction<NegotiationRoundDto>) => void;
    setNegotiationContext: (state: State, action: PayloadAction<NegotiationContextDto>) => void;
  },
  "basicSourcingEvent"
>({
  name: "basicSourcingEvent",
  initialState,
  reducers: {
    setSourcingEvent: (state, action) => {
      state.state = action.payload ? mapDtoToSourcingEventState(action.payload) : null;
    },
    selectBid: (state, action) => {
      const { bid, productId, group, negotiationRoundId } = action.payload;
      const groupBids = getSelectedGroupBids(state, group.id, negotiationRoundId);
      addBidId(findOrCreateProductBids(groupBids, productId), bid.id);
    },
    selectAllBids: (state, action) => {
      const { bid, negotiationRoundId } = action.payload;
      bid.products?.forEach((groupBid) => {
        selectBidForGroupProducts(state, groupBid, bid.id, negotiationRoundId);
      });
    },
    selectAllBidsInGroup: (state, action) => {
      const { bid, groupBid, negotiationRoundId } = action.payload;
      selectBidForGroupProducts(state, groupBid, bid.id, negotiationRoundId);
    },
    removeBid: (state, action) => {
      const { bid, productId, group, negotiationRoundId } = action.payload;
      const groupBids = getSelectedGroupBids(state, group.id, negotiationRoundId);
      removeBidId(findOrCreateProductBids(groupBids, productId), bid.id);
    },
    setSupplierQuestionStatus: (state, action) => {
      const question = state.state?.event.questions?.find((q) => q.id === action.payload.question.id);
      if (question) question.status = action.payload.status;
    },
    setNegotiationRound: (state, action) => {
      if (!state.state?.event) return;

      if (!state.state.event.negotiationContext) {
        state.state.event.negotiationContext = {
          rounds: [action.payload],
        };
        return;
      }

      const roundIndex = state.state.event.negotiationContext.rounds.findIndex((r) => r.id === action.payload.id);

      if (roundIndex >= 0) {
        state.state.event.negotiationContext.rounds[roundIndex] = action.payload;
      } else {
        state.state.event.negotiationContext.rounds.push(action.payload);
      }
    },
    setNegotiationContext: (state, action) => {
      if (state.state?.event) state.state.event.negotiationContext = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadBseThunk.pending, (state, action) => {
      state.requestedEventId = action.meta.arg.eventId;
    });
    builder.addCase(loadBseThunk.fulfilled, (state, action) => {
      if (action.meta.arg.eventId !== state.requestedEventId) {
        return;
      }
      if (state.state && action.payload && isOlderRevisionOfSameEntity(action.payload, state.state)) {
        return;
      }
      state.state = action.payload;
    });
    builder.addCase(editBseThunk.fulfilled, (state, action) => {
      const payload = action.payload;
      if (
        payload &&
        (!state.state || payload.id !== state.state.id || isOlderRevision(payload, state.state))
      ) {
        return;
      }
      state.state = payload;
    });
  },
});

export const {
  selectBid,
  selectAllBids,
  selectAllBidsInGroup,
  removeBid,
  setSourcingEvent,
  setSupplierQuestionStatus,
  setNegotiationRound,
  setNegotiationContext,
} = slice.actions;
export default slice.reducer;

const getSelectedGroupBids = (state: State, groupId: string, negotiationRoundId?: string): WinningProductGroupBidsDto => {
  const selectedProductGroupBids = negotiationRoundId
    ? getOrInitRoundSelections(state, negotiationRoundId)
    : state.state?.event.publishedFields?.selectedProductGroupBids;

  return findOrCreateGroupBids(selectedProductGroupBids ?? [], groupId);
};

const getOrInitRoundSelections = (state: State, negotiationRoundId: string): WinningProductGroupBidsDto[] => {
  const round = state.state?.event.negotiationContext?.rounds.find((r) => r.id === negotiationRoundId);
  if (!round) return [];
  if (!round.selectedProductBids) round.selectedProductBids = [];
  return round.selectedProductBids;
};

const selectBidForGroupProducts = (
  state: State,
  groupBid: ProductGroupBidDto,
  bidId: string,
  negotiationRoundId?: string,
) => {
  const selectedGroupBids = getSelectedGroupBids(state, groupBid.productGroupId, negotiationRoundId);
  groupBid.productBids.forEach((pb) => {
    addBidId(findOrCreateProductBids(selectedGroupBids, pb.productId), bidId);
  });
};

const mapDtoToSourcingEventState = (dto: BseDto): BasicSourcingEventState => ({
  id: dto.id,
  event: dto,
  lastChanged: dto.modifiedAt,
  title: {
    value: dto.title,
    errorMessage: null,
  },
  description: {
    value: mapDescription(dto),
    errorMessage: null,
  },
  deadline: {
    value: mapDeadline(dto),
    errorMessage: null,
  },
  timezone: {
    value: dto.timezone,
    errorMessage: null,
  },
  referenceId: {
    value: dto.referenceId ?? null,
    errorMessage: null,
  },
  emailInvites: {
    value: dto.emailInvites,
    errorMessage: null,
  },
  organizationInvites: {
    value: dto.organizationInvites,
    errorMessage: null,
  },
  documents: {
    value: dto.documents,
    errorMessage: null,
  },
  uploads: {
    value: mapUploads(dto),
    errorMessage: null,
  },
  projects: {
    values: dto.projects,
    errorMessage: null,
  },
});

const mapDescription = (dto: BseDto) => dto.publishedFields?.description ?? dto.publishedFields?.description ?? null;
const mapDeadline = (dto: BseDto) => dto.publishedFields?.deadline ?? dto.draftFields?.deadline;
const mapUploads = (dto: BseDto) => dto.publishedFields?.uploads ?? dto.draftFields?.uploads ?? [];
